// @ts-nocheck
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
// import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField"; // Store Custom Code
import debounce from "lodash/debounce";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
// import { productLineProps } from "@store/Themes/common/props/productLine"; // Store Custom Code
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import QuantityAdjustButtons from "@store/Library/Components/QuantityAdjustButtons/QuantityAdjustButtons";
import TextField, { TextFieldPresentationProps } from "@store/MobiusOverrides/TextField/TextField";
import { productLineProps } from "@store/Themes/common/props/productLine";

const minQty = 0;
const maxQty = 999999;
/* Store Custom Code - End */

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    ...productLineProps.quantity,
} as CartLineQuantityStyles; /* Store Custom Code */

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => { },
    extendedStyles,
}) => {
    /* Store Custom Code - Start */
    const localOnQtyOrderedChange = React.useCallback(
        (value: string, onError?: () => void) => {
            const inputQty = parseFloat(value);
            if (Number.isNaN(inputQty)) {
                onError?.();
                return;
            }

            onQtyOrderedChange(inputQty);
        },
        [onQtyOrderedChange],
    );
    /* Store Custom Code - End */

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));
    /* Store Custom Code - Start */
    const isDisabled = !cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id];

    const debouncedQtyOrderedChange = React.useMemo(
        () => debounce((nextValue: string) => localOnQtyOrderedChange(nextValue), 500),
        [localOnQtyOrderedChange],
    );

    React.useEffect(() => {
        return () => debouncedQtyOrderedChange.cancel();
    }, [debouncedQtyOrderedChange]);

    const getCurrentQty = () => {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? cartLine.qtyOrdered! : parsed;
    };

    const updateQty = (nextValue: string) => {
        changeHandler({ currentTarget: { value: nextValue } });
        debouncedQtyOrderedChange(nextValue);
    };

    const changeQuantityBy = (delta: number) => {
        const baseQty = getCurrentQty();
        const nextQty = Math.min(maxQty, Math.max(minQty, baseQty + delta));

        updateQty(nextQty.toString());
    };

    const renderControlButtons = () => (
        <QuantityAdjustButtons
            decrementDisabled={isDisabled || Number(value || cartLine.qtyOrdered) <= minQty}
            onDecrement={() => changeQuantityBy(-1)}
            incrementDisabled={isDisabled || Number(value || cartLine.qtyOrdered) >= maxQty}
            onIncrement={() => changeQuantityBy(1)}
        />
    );
    /* Store Custom Code - End */

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <TextField
                {...styles.editableQuantityTextField}
                type="number"
                min={minQty /* Store Custom Code */}
                max={maxQty /* Store Custom Code */}
                label={label || translate("QTY")}
                value={value}
                disabled={isDisabled /* Store Custom Code */}
                onChange={changeHandler}
                onKeyDown={keyDownHandler}
                onBlur={blurHandler}
                onClick={event => event.currentTarget.focus()}
                data-test-selector="cartline_qty"
                // Store Custom Code
                renderControlButtons={renderControlButtons}
            />
        );
    }

    return (
        <SmallHeadingAndText
            {...styles.readOnlyQuantityText}
            heading={label || translate("QTY")}
            text={cartLine.qtyOrdered!}
            data-test-selector="quoteDetail_cardLine_qty"
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
